import React from 'react'
import { Box, BoxProps } from '@chakra-ui/react'
import Image from 'next/image'
import { AspectRatioOption, ImgixParams, toImgixUrl } from 'lib/imgix'

type FeatureImageProps = {
    src: string
    alt?: string
    sizes?: string
    aspectRatio?: AspectRatioOption
    imgixParams?: ImgixParams
} & BoxProps

export const defaultImgixParams = {
    fit: 'crop',
    crop: 'center',
} as ImgixParams

function getAspectRatioForCSS(ar: AspectRatioOption) {
    return ar.replace(':', '/')
}

/**
 * The generic "Feature" (a.k.a. "Hero") image to be used across the new FE app.
 *
 * @param src - the path to the image
 * @param alt - a text description of the image, which isn't mandatory
 *              but is incredibly useful for accessibility
 * @param sizes - how wide the image will be at different breakpoints
 * @param aspectRatio - a preferred aspect ratio for this image's box
 *                      (image's `width` is equal to the parent's one)
 * @param imgixParams - to tune up other Imgix parameters
 * @param boxProps - Chakra's `BoxProps` to tune up the parent's layout
 */
export const FeatureImage = ({
    src,
    alt,
    sizes = '100vw',
    aspectRatio = '16:9',
    imgixParams,
    ...boxProps
}: FeatureImageProps) => {
    // IMPLEMENTATION NOTES:
    // - All defaults values (`sizes`, `aspectRatio`, `quality`) are made explicit.
    // - The `width` and `height`, otherwise mandatory, not needed w/ `layout="fill"`.
    // - The `sizes` are mandatory for `layout="fill"` + crucially affect performance,
    //   (w/o sizes, the image will be sent AT TWICE the necessary resolution — check
    //   https://nextjs.org/docs/api-reference/next/image#sizes).
    // - The built-in loader for Imgix is insufficient, so we are to define the custom
    //   one (https://nextjs.org/docs/api-reference/next/image#loader) & pass it those
    //   params that come from the `next/image` (see below).
    const imgixLoader = ({
        src,
        width,
        quality = 75,
    }: {
        src: string
        width: number
        quality?: number
    }) => {
        const params = {
            w: width,
            q: quality,
            ar: aspectRatio,
            ...(imgixParams ?? defaultImgixParams),
        }
        return toImgixUrl(src, params)
    }

    return (
        <Box
            position="relative"
            width="100%"
            sx={{ aspectRatio: getAspectRatioForCSS(aspectRatio) }}
            {...boxProps}
        >
            <Image
                src={src}
                alt={alt}
                layout="fill"
                loader={imgixLoader}
                sizes={sizes}
                objectFit="cover"
                objectPosition="50% 50%"
            />
        </Box>
    )
}
